import { updateBrand, useBrand, type BrandForm as BrandFormValues } from '@gozoki/api'
import type { Brand } from '@gozoki/api-types'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import z from 'zod/v3'
import { BrandForm } from '~/components/routes/categories/BrandForm'
import { Spinner } from '~/components/ui/spinner'
import { getAxiosErrorMessage } from '~/lib/utils'
import { useMigratedRight, withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/categories/brand/$brandId/edit')({
  staticData: {
    permission: ['edit', 'categories.brands.brand'],
  },
  params: {
    parse: z.object({ brandId: z.coerce.number() }).parse,
  },
  component: EditBrandPage,
})
withRoutePermissions(Route)

const toFormValues = (brand: Brand): BrandFormValues => ({
  name: brand.name,
  image: null,
})

function EditBrandPage() {
  const { brandId } = Route.useParams()
  const navigate = useNavigate()
  const canEdit = useMigratedRight(['edit', 'categories.brands.brand'], ['ADMIN'])

  const { data: brand, isLoading } = useBrand(brandId)
  const { mutateAsync: update } = useMutation({ mutationFn: updateBrand })

  const navigateBack = () => navigate({ to: '/categories/brands' })

  if (isLoading) {
    return (
      <div className="flex justify-center p-16">
        <Spinner />
      </div>
    )
  }

  if (!brand) {
    return <div className="text-muted-foreground p-16 text-center">Marque introuvable.</div>
  }

  return (
    <BrandForm
      key={brand.id}
      title={canEdit ? 'Modifier une marque' : 'Voir une marque'}
      submitLabel="Modifier"
      canEdit={canEdit}
      imageDefaultName={brand.image?.name}
      defaultValues={toFormValues(brand)}
      onCancel={navigateBack}
      onSubmit={async (values) => {
        try {
          await update({ id: brandId, data: values })
          navigateBack()
        } catch (error) {
          toast.error(getAxiosErrorMessage(error))
        }
      }}
    />
  )
}
